import { useEffect, useState } from 'react'
import { Warning, SignOut } from '@phosphor-icons/react'
import { useAuth } from '../context/AuthContext'

function formatRemaining(secondsRemaining: number): string {
  if (secondsRemaining <= 0) return 'expirado'
  const m = Math.floor(secondsRemaining / 60)
  const s = secondsRemaining % 60
  if (m <= 0) return `${s}s`
  return `${m}m ${s}s`
}

export function ImpersonationBanner() {
  const { isImpersonating, impersonationExpiresAt, user, stopImpersonation } = useAuth()
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000))
  const [stopping, setStopping] = useState(false)

  useEffect(() => {
    if (!isImpersonating) return
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000)
    return () => clearInterval(t)
  }, [isImpersonating])

  const remaining = impersonationExpiresAt ? impersonationExpiresAt - now : 0

  // Auto-stop when token expires
  useEffect(() => {
    if (isImpersonating && impersonationExpiresAt && remaining <= 0 && !stopping) {
      setStopping(true)
      stopImpersonation().finally(() => setStopping(false))
    }
  }, [isImpersonating, impersonationExpiresAt, remaining, stopping, stopImpersonation])

  if (!isImpersonating) return null

  async function handleStop() {
    setStopping(true)
    try {
      await stopImpersonation()
    } finally {
      setStopping(false)
    }
  }

  return (
    <div
      className="sticky top-0 z-[100] w-full bg-red-600 text-white px-4 py-2 flex items-center justify-between shadow-md"
      role="alert"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Warning size={20} weight="fill" className="flex-shrink-0" />
        <div className="text-sm min-w-0">
          <span className="font-semibold">Modo impersonação ativo</span>
          <span className="mx-2">•</span>
          <span className="truncate">
            Você é <strong>{user?.name || user?.email}</strong> ({user?.email})
          </span>
          <span className="mx-2 opacity-75">•</span>
          <span className="opacity-90">expira em {formatRemaining(remaining)}</span>
        </div>
      </div>
      <button
        onClick={handleStop}
        disabled={stopping}
        className="flex items-center gap-1.5 px-3 py-1 bg-white text-red-600 rounded-md font-medium text-sm hover:bg-red-50 transition-colors disabled:opacity-50"
      >
        <SignOut size={16} weight="bold" />
        {stopping ? 'Saindo...' : 'Sair'}
      </button>
    </div>
  )
}
